import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatIconModule } from '@angular/material/icon'
import {
  AppointmentSelectOption,
  ChargeGrouping,
  DirectivesModule,
  EstimateChargeDiscountType,
  EstimateFormGroup,
  PatientInsurance,
} from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { CharityCareSummaryComponent } from '../../charity-care-summary/charity-care-summary.component'
import { InsurancesCardModule } from '../../insurances-card/insurances-card.module'
import { BalanceSendChannel } from '../balance-summary.types'
import { BalanceBenefitsOptionsMenuComponent } from '../components/balance-benefits-options-menu/balance-benefits-options-menu.component'
import { BalanceBenefitsPanelComponent } from '../components/balance-benefits-panel/balance-benefits-panel.component'
import { BalanceChargesPanelComponent } from '../components/balance-charges-panel/balance-charges-panel.component'
import { BalanceChargesResetType } from '../components/balance-charges-toolbar/balance-charges-toolbar.component'
import { EstimateReviewMultiVisitComponent } from '../review-multi-visit/estimate-review-multi-visit.component'
import { type BalanceChargeBreakdownDialogData } from '../review-multi-visit/components/balance-charge-breakdown-dialog/balance-charge-breakdown-dialog.component'
import { BenefitsFormGroup } from '../services/estimate-balance-form.service'
import { EstimateBalanceViewModel } from '../services/estimate-balance-presenter.service'
import { ModifySessionActionBarComponent } from './components/modify-session-action-bar/modify-session-action-bar.component'
import { ModifySessionHeaderComponent } from './components/modify-session-header/modify-session-header.component'
import { ModifySessionSectionComponent } from './components/modify-session-section/modify-session-section.component'

@Component({
  selector: 'mmx-estimate-modify-session',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DirectivesModule,
    MatButtonModule,
    MatCardModule,
    MatExpansionModule,
    MatIconModule,
    InsurancesCardModule,
    BalanceChargesPanelComponent,
    CharityCareSummaryComponent,
    BalanceBenefitsOptionsMenuComponent,
    BalanceBenefitsPanelComponent,
    EstimateReviewMultiVisitComponent,
    ModifySessionActionBarComponent,
    ModifySessionHeaderComponent,
    ModifySessionSectionComponent,
  ],
  templateUrl: './estimate-modify-session.component.html',
  styleUrls: ['./estimate-modify-session.component.scss'],
})
export class EstimateModifySessionComponent {
  @Input({ required: true }) vm!: EstimateBalanceViewModel
  @Input() busy = false
  @Input({ required: true }) estimateForm!: EstimateFormGroup
  @Input({ required: true }) benefitsForm!: BenefitsFormGroup
  @Input() appointmentOptions: AppointmentSelectOption[] = []

  @Output() readonly modify = new EventEmitter<void>()
  @Output() readonly discard = new EventEmitter<void>()
  @Output() readonly finalize = new EventEmitter<void>()
  @Output() readonly calculate = new EventEmitter<void>()
  @Output() readonly viewHistory = new EventEmitter<void>()
  @Output() readonly void = new EventEmitter<void>()
  @Output() readonly send = new EventEmitter<BalanceSendChannel>()
  @Output() readonly sendDocument = new EventEmitter<void>()
  @Output() readonly printLetter = new EventEmitter<void>()
  @Output() readonly chargesChanged = new EventEmitter<void>()
  @Output() readonly chargesReset = new EventEmitter<BalanceChargesResetType>()
  @Output() readonly templateSelected = new EventEmitter<string>()
  @Output() readonly appointmentSelectionChange = new EventEmitter<string[]>()
  @Output() readonly cocModeChange = new EventEmitter<boolean>()
  @Output() readonly unscheduledChange = new EventEmitter<boolean>()
  @Output() readonly addDiscount = new EventEmitter<{ index: number; type: EstimateChargeDiscountType }>()
  @Output() readonly removeDiscount = new EventEmitter<{ index: number }>()
  @Output() readonly benefitsChanged = new EventEmitter<void>()
  @Output() readonly primaryInsuranceChange = new EventEmitter<PatientInsurance>()
  @Output() readonly secondaryInsuranceChange = new EventEmitter<PatientInsurance>()
  @Output() readonly pbTrace = new EventEmitter<ChargeGrouping>()
  @Output() readonly hbTrace = new EventEmitter<ChargeGrouping>()
  @Output() readonly chargeBreakdown = new EventEmitter<BalanceChargeBreakdownDialogData>()

  get readOnly(): boolean {
    return this.vm?.modifySession?.phase === 'finalized'
  }

  get chargesExpandedByDefault(): boolean {
    return this.vm?.modifySession?.phase === 'modifyPreCalc'
  }
}
